import { Link, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AlertCircle,
  ArrowLeft,
  Globe,
  Loader2,
  Radio,
  User,
  Users,
} from "lucide-react";
import {
  useTeam,
  useRemoveTeamMember,
  useTeamStats,
} from "@/hooks/useTeams";
import { useAuth } from "@/hooks/useAuth";
import { usePageMeta } from "@/hooks/usePageMeta";
import MemberTable from "@/components/teams/MemberTable";
import AddMemberForm from "@/components/teams/AddMemberForm";
import TeamStatsSection from "@/components/teams/TeamStatsSection";
import RecordingsTab from "@/components/teams/RecordingsTab";
import { formatUserName } from "@/utils/userName";

type TabId = "overview" | "members" | "recordings";

const TAB_IDS: readonly TabId[] = ["overview", "members", "recordings"];

export default function TeamPage() {
  const { teamId = "" } = useParams<{ teamId: string }>();
  const { t, i18n } = useTranslation("pages");
  const { user } = useAuth();
  const { data: team, isLoading, error } = useTeam(teamId);
  const { data: teamStats } = useTeamStats(teamId, !!team?.members);
  usePageMeta({ title: team?.name, description: team?.description });
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const isMember =
    !!user &&
    !!team &&
    (team.ownerId === user.id ||
      (team.members ?? []).some(
        (m) => m.userId === user.id && m.status === "active",
      ));
  const validTabs: readonly TabId[] = isMember
    ? TAB_IDS
    : TAB_IDS.filter((id) => id !== "recordings");
  const activeTab: TabId = validTabs.includes(tabParam as TabId)
    ? (tabParam as TabId)
    : "overview";
  const setActiveTab = (tab: TabId) => {
    setSearchParams(
      (prev) => {
        prev.set("tab", tab);
        return prev;
      },
      { replace: true },
    );
  };
  const removeMember = useRemoveTeamMember(teamId);

  if (isLoading) {
    return (
      <div className="page-container flex min-h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
        <span className="ml-2 text-secondary-600">{t("teams.loading")}</span>
      </div>
    );
  }

  if (error || !team) {
    return (
      <div className="page-container flex min-h-[50vh] items-center justify-center">
        <div className="text-center">
          <AlertCircle className="mx-auto mb-4 h-12 w-12 text-red-500" />
          <h2 className="mb-2 text-xl font-semibold text-secondary-900">
            {t("teams.teamNotFound")}
          </h2>
          <Link to="/teams" className="text-primary-600 hover:underline">
            {t("teams.backToTeams")}
          </Link>
        </div>
      </div>
    );
  }

  const canManage =
    !!user &&
    (team.ownerId === user.id ||
      (team.members ?? []).some(
        (m) =>
          m.userId === user.id && m.role === "director" && m.status === "active",
      ));
  const memberCount = team.memberCount ?? team.members?.length ?? 0;
  const invitedCount = (team.members ?? []).filter(
    (m) => m.status === "invited",
  ).length;

  return (
    <div className="page-container">
      <Link
        to="/teams"
        className="mb-4 inline-flex items-center gap-1 text-sm text-secondary-500 hover:text-secondary-800"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        {t("teams.backToTeams")}
      </Link>

      <div className="mb-1 flex items-start justify-between gap-3">
        <h1 className="page-title">{team.name}</h1>
        <Link
          to={`/teams/${teamId}/live`}
          className="btn-primary inline-flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap px-3 py-1.5 text-sm"
        >
          <Radio className="h-4 w-4" aria-hidden="true" />
          {t("teams.watchLive")}
        </Link>
      </div>
      {team.description && (
        <p className="mb-4 text-secondary-600">{team.description}</p>
      )}

      <div className="mb-6 flex border-b border-secondary-200">
        {(
          [
            { id: "overview" as TabId, label: t("teams.tabOverview") },
            { id: "members" as TabId, label: t("teams.tabMembers") },
            ...(isMember
              ? [{ id: "recordings" as TabId, label: t("teams.tabRecordings") }]
              : []),
          ] as const
        ).map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? "border-primary-600 text-primary-600"
                : "border-transparent text-secondary-600 hover:text-secondary-900"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "overview" && (
        <div className="space-y-4 text-sm">
          {teamStats && <TeamStatsSection stats={teamStats} />}
          <div className="flex items-center text-secondary-600">
            <User className="mr-2 h-4 w-4" aria-hidden="true" />
            {t("teams.owner")}: {formatUserName(team.owner)}
          </div>
          <div className="flex items-center text-secondary-600">
            <Users className="mr-2 h-4 w-4" aria-hidden="true" />
            {t("teams.members", { count: memberCount })}
          </div>
          {team.url && (
            <div className="flex items-center text-secondary-600">
              <Globe className="mr-2 h-4 w-4" aria-hidden="true" />
              <a
                href={team.url}
                target="_blank"
                rel="noopener noreferrer"
                className="truncate text-primary-600 hover:underline"
              >
                {team.url}
              </a>
            </div>
          )}
          <div className="border-t border-secondary-200 pt-4 text-secondary-500">
            {t("teams.created", {
              date: new Date(team.createdAt).toLocaleDateString(i18n.language),
            })}
          </div>
        </div>
      )}

      {activeTab === "members" && (
        <div>
          {team.members ? (
            <>
              <div className="mb-3 text-sm text-secondary-600">
                {t("teams.members", { count: memberCount })}
                {invitedCount > 0 && (
                  <> · {t("teams.invitedCount", { count: invitedCount })}</>
                )}
              </div>
              {canManage && <AddMemberForm teamId={teamId} />}
              {removeMember.isError && (
                <p className="mb-3 text-sm text-red-600" role="alert">
                  {t("teams.removeFailed")}
                </p>
              )}
              <MemberTable
                team={team}
                currentUserId={user?.id}
                canManage={canManage}
                onRemove={(userId) => removeMember.mutate(userId)}
                removingUserId={
                  removeMember.isPending ? removeMember.variables : null
                }
              />
            </>
          ) : (
            <p className="text-sm text-secondary-500">
              {t("teams.memberListPrivate")}
            </p>
          )}
        </div>
      )}

      {activeTab === "recordings" && isMember && (
        <RecordingsTab
          teamId={teamId}
          canManage={canManage}
          members={(team.members ?? []).filter((m) => m.status === "active")}
        />
      )}
    </div>
  );
}
